import type { Condition } from '@character-creator/types';

/**
 * All 15 conditions from the D&D 5e SRD 5.1
 * These conditions alter a creature's capabilities and can arise from
 * spells, class features, monster attacks, or other effects.
 */
export const conditions: Condition[] = [
  {
    id: 'blinded',
    name: 'Blinded',
    description:
      'A blinded creature can\'t see and automatically fails any ability check that requires sight.',
    effects: [
      'Can\'t see and automatically fails any ability check that requires sight.',
      'Attack rolls against the creature have advantage.',
      'The creature\'s attack rolls have disadvantage.',
    ],
    commonSources: [
      'Blindness/Deafness spell',
      'Color Spray spell',
      'Darkness spell (if lacking darkvision)',
      'Sunburst spell',
      'Looking at a medusa',
    ],
  },
  {
    id: 'charmed',
    name: 'Charmed',
    description:
      'A charmed creature can\'t attack the charmer or target the charmer with harmful abilities or magical effects.',
    effects: [
      'Can\'t attack the charmer or target the charmer with harmful abilities or magical effects.',
      'The charmer has advantage on any ability check to interact socially with the creature.',
    ],
    commonSources: [
      'Charm Person spell',
      'Charm Monster spell',
      'Hypnotic Pattern spell',
      'Vampire\'s Charm ability',
      'Succubus/Incubus Charm',
    ],
  },
  {
    id: 'deafened',
    name: 'Deafened',
    description: 'A deafened creature can\'t hear and automatically fails any ability check that requires hearing.',
    effects: [
      'Can\'t hear and automatically fails any ability check that requires hearing.',
    ],
    commonSources: [
      'Blindness/Deafness spell',
      'Thunderwave spell (DM discretion)',
      'Extremely loud environments',
    ],
  },
  {
    id: 'exhaustion',
    name: 'Exhaustion',
    description:
      'Some special abilities and environmental hazards, such as starvation and the long-term effects of freezing or scorching temperatures, can lead to a special condition called exhaustion. Exhaustion is measured in six levels.',
    effects: [
      'Exhaustion effects are cumulative.',
      'An effect that removes exhaustion reduces its level by 1.',
      'Finishing a long rest reduces exhaustion by 1 level (if food and drink are available).',
      'Being reduced to 0 hit points increases exhaustion by 1 level (optional rule).',
    ],
    levels: [
      { level: 1, effect: 'Disadvantage on ability checks' },
      { level: 2, effect: 'Speed halved' },
      { level: 3, effect: 'Disadvantage on attack rolls and saving throws' },
      { level: 4, effect: 'Hit point maximum halved' },
      { level: 5, effect: 'Speed reduced to 0' },
      { level: 6, effect: 'Death' },
    ],
    commonSources: [
      'Going without food or water',
      'Extreme heat or cold',
      'Forced march without rest',
      'Certain monster abilities (e.g., Ghost\'s Horrifying Visage)',
      'Some spells (e.g., Sickening Radiance)',
      'Berserker Barbarian\'s Frenzy feature',
    ],
  },
  {
    id: 'frightened',
    name: 'Frightened',
    description:
      'A frightened creature has disadvantage on ability checks and attack rolls while the source of its fear is within line of sight.',
    effects: [
      'Has disadvantage on ability checks and attack rolls while the source of its fear is within line of sight.',
      'Can\'t willingly move closer to the source of its fear.',
    ],
    commonSources: [
      'Cause Fear spell',
      'Fear spell',
      'Wrathful Smite spell',
      'Dragon\'s Frightful Presence',
      'Intimidation (optional rule)',
    ],
  },
  {
    id: 'grappled',
    name: 'Grappled',
    description:
      'A grappled creature\'s speed becomes 0, and it can\'t benefit from any bonus to its speed.',
    effects: [
      'Speed becomes 0 and can\'t benefit from any bonus to speed.',
      'The condition ends if the grappler is incapacitated.',
      'The condition also ends if an effect removes the grappled creature from the reach of the grappler or grappling effect (such as when a creature is hurled away by the Thunderwave spell).',
    ],
    commonSources: [
      'Grappling action in combat',
      'Entangle spell',
      'Some monster attacks (e.g., Giant Constrictor Snake)',
      'Web spell',
    ],
  },
  {
    id: 'incapacitated',
    name: 'Incapacitated',
    description: 'An incapacitated creature can\'t take actions or reactions.',
    effects: [
      'Can\'t take actions or reactions.',
    ],
    commonSources: [
      'Being unconscious (includes incapacitated)',
      'Certain spells and effects',
      'Hypnotic Pattern spell',
    ],
  },
  {
    id: 'invisible',
    name: 'Invisible',
    description:
      'An invisible creature is impossible to see without the aid of magic or a special sense. For the purpose of hiding, the creature is heavily obscured. The creature\'s location can be detected by any noise it makes or any tracks it leaves.',
    effects: [
      'Impossible to see without magic or special sense.',
      'For hiding purposes, the creature is heavily obscured.',
      'Location can be detected by noise or tracks.',
      'Attack rolls against the creature have disadvantage.',
      'The creature\'s attack rolls have advantage.',
    ],
    commonSources: [
      'Invisibility spell',
      'Greater Invisibility spell',
      'Potion of Invisibility',
      'Some monster abilities',
      'Ring of Invisibility',
    ],
  },
  {
    id: 'paralyzed',
    name: 'Paralyzed',
    description: 'A paralyzed creature is incapacitated and can\'t move or speak.',
    effects: [
      'Is incapacitated (can\'t take actions or reactions).',
      'Can\'t move or speak.',
      'Automatically fails Strength and Dexterity saving throws.',
      'Attack rolls against the creature have advantage.',
      'Any attack that hits the creature is a critical hit if the attacker is within 5 feet.',
    ],
    includesConditions: ['incapacitated'],
    commonSources: [
      'Hold Person spell',
      'Hold Monster spell',
      'Ghoul\'s Claws attack',
      'Some poisons',
    ],
  },
  {
    id: 'petrified',
    name: 'Petrified',
    description:
      'A petrified creature is transformed, along with any nonmagical object it is wearing or carrying, into a solid inanimate substance (usually stone). Its weight increases by a factor of ten, and it ceases aging.',
    effects: [
      'Transformed into solid inanimate substance (usually stone).',
      'Weight increases by a factor of ten.',
      'Ceases aging.',
      'Is incapacitated (can\'t take actions or reactions).',
      'Can\'t move or speak.',
      'Is unaware of its surroundings.',
      'Attack rolls against the creature have advantage.',
      'Automatically fails Strength and Dexterity saving throws.',
      'Has resistance to all damage.',
      'Immune to poison and disease (existing poison/disease suspended, not neutralized).',
    ],
    includesConditions: ['incapacitated'],
    commonSources: [
      'Flesh to Stone spell',
      'Medusa\'s Petrifying Gaze',
      'Basilisk\'s Petrifying Gaze',
      'Cockatrice\'s Bite',
    ],
  },
  {
    id: 'poisoned',
    name: 'Poisoned',
    description: 'A poisoned creature has disadvantage on attack rolls and ability checks.',
    effects: [
      'Has disadvantage on attack rolls and ability checks.',
    ],
    commonSources: [
      'Ray of Sickness spell',
      'Poison damage from various sources',
      'Giant Spider\'s Bite',
      'Assassin\'s poison weapons',
      'Contagion spell',
    ],
  },
  {
    id: 'prone',
    name: 'Prone',
    description:
      'A prone creature\'s only movement option is to crawl, unless it stands up and thereby ends the condition.',
    effects: [
      'Only movement option is to crawl, unless it stands up.',
      'Standing up costs an amount of movement equal to half your speed.',
      'Has disadvantage on attack rolls.',
      'An attack roll against the creature has advantage if the attacker is within 5 feet.',
      'An attack roll against the creature has disadvantage if the attacker is more than 5 feet away.',
    ],
    commonSources: [
      'Being knocked down',
      'Dropping prone voluntarily',
      'Grease spell',
      'Command spell (with "grovel")',
      'Shove action',
    ],
  },
  {
    id: 'restrained',
    name: 'Restrained',
    description: 'A restrained creature\'s speed becomes 0, and it can\'t benefit from any bonus to its speed.',
    effects: [
      'Speed becomes 0 and can\'t benefit from any bonus to speed.',
      'Attack rolls against the creature have advantage.',
      'The creature\'s attack rolls have disadvantage.',
      'The creature has disadvantage on Dexterity saving throws.',
    ],
    commonSources: [
      'Entangle spell',
      'Web spell',
      'Grasping Vine spell',
      'Net (attack)',
      'Some monster abilities (e.g., Giant Spider\'s Web)',
    ],
  },
  {
    id: 'stunned',
    name: 'Stunned',
    description:
      'A stunned creature is incapacitated, can\'t move, and can speak only falteringly.',
    effects: [
      'Is incapacitated (can\'t take actions or reactions).',
      'Can\'t move, and can speak only falteringly.',
      'Automatically fails Strength and Dexterity saving throws.',
      'Attack rolls against the creature have advantage.',
    ],
    includesConditions: ['incapacitated'],
    commonSources: [
      'Stunning Strike (Monk)',
      'Power Word Stun spell',
      'Divine Word spell',
      'Symbol spell',
      'Some monster abilities',
    ],
  },
  {
    id: 'unconscious',
    name: 'Unconscious',
    description:
      'An unconscious creature is incapacitated, can\'t move or speak, and is unaware of its surroundings.',
    effects: [
      'Is incapacitated (can\'t take actions or reactions).',
      'Can\'t move or speak, and is unaware of its surroundings.',
      'Drops whatever it\'s holding and falls prone.',
      'Automatically fails Strength and Dexterity saving throws.',
      'Attack rolls against the creature have advantage.',
      'Any attack that hits the creature is a critical hit if the attacker is within 5 feet.',
    ],
    includesConditions: ['incapacitated', 'prone'],
    commonSources: [
      'Dropping to 0 hit points',
      'Sleep spell',
      'Being put to sleep by other means',
      'Power Word Kill (if killed)',
      'Certain poisons',
    ],
  },
];

// Lookup map for efficient access
export const conditionMap = new Map<string, Condition>(
  conditions.map((c) => [c.id, c])
);

// Helper function
export function getCondition(id: string): Condition | undefined {
  return conditionMap.get(id);
}
